import { Check, Clock } from "lucide-react";
import { Slider } from "antd";
import { ReactSVG } from "react-svg";
import { NEW_CUSTOMER_BILLING_COMPACT_LINE } from "../../constants/newCustomerBilling";
import {
  SUBSCRIBE_MODULES,
  computeSubscribeMonthlyTotal,
  getProviderTheme,
  getSubscribeIntro,
  isPayrollSubscribeEligible,
} from "../../utils/subscribeModules";

const RETENTION_PRESETS = [
  { days: 7, label: "7 Days", extraDays: 0, extraCost: 0 },
  { days: 14, label: "14 Days", extraDays: 7, extraCost: 14 },
  { days: 21, label: "21 Days", extraDays: 14, extraCost: 28 },
  { days: 30, label: "30 Days", extraDays: 23, extraCost: 46 },
  { days: 60, label: "60 Days", extraDays: 53, extraCost: 106 },
];

const RETENTION_SLIDER_MARKS = {
  7: {
    style: { color: "#16a34a", fontWeight: "600" },
    label: "7d",
  },
  14: "14d",
  21: "21d",
  30: "30d",
  45: "45d",
  60: {
    style: { color: "#2563eb", fontWeight: "600" },
    label: "60d",
  },
};

export function ProviderBadge({ provider }) {
  const theme = getProviderTheme(provider);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${theme.badge}`}
    >
      <span
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: theme.primary }}
        aria-hidden
      />
      {theme.label}
    </span>
  );
}

function ModuleCheckbox({
  inputName,
  checked,
  disabled,
  lockedRequired = false,
  onChange,
  theme,
}) {
  if (lockedRequired) {
    return (
      <>
        <input
          type="checkbox"
          name={inputName}
          checked={checked}
          disabled
          readOnly
          className="sr-only"
          aria-hidden
        />
        <span
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-lg shadow-xs"
          style={{ backgroundColor: theme.light, color: theme.primary }}
          aria-hidden
        >
          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
        </span>
      </>
    );
  }

  return (
    <input
      type="checkbox"
      name={inputName}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className={`h-4.5 w-4.5 shrink-0 rounded-md border-gray-300 transition-colors focus:outline-none focus:ring-0 ${
        disabled ? "cursor-not-allowed opacity-50 bg-gray-100" : "cursor-pointer"
      }`}
      style={{ accentColor: theme.primary }}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

function ModuleIcon({ module, selected, disabled, theme }) {
  return (
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors duration-200"
      style={{
        backgroundColor: selected ? theme.light : "#f3f4f6",
      }}
    >
      {module.icon ? (
        <ReactSVG
          src={module.icon}
          className={`h-5 w-5 ${selected ? "[&_path]:fill-current" : "[&_path]:fill-gray-500"}`}
          style={selected ? { color: theme.primary } : undefined}
        />
      ) : null}
    </div>
  );
}

function ModuleOptionCard({
  moduleKey,
  module,
  checked,
  disabled,
  lockedRequired = false,
  onChange,
  provider,
  layout = "inline",
}) {
  const theme = getProviderTheme(provider);
  const selected = checked && !disabled;
  const inputName = moduleKey === "accounts" ? "accounts" : moduleKey;
  const isStacked = layout === "stacked";

  const cardClass = [
    "relative w-full rounded-2xl border-2 p-4 sm:p-4.5 text-left transition-all duration-200",
    isStacked
      ? "flex flex-col justify-between min-h-[8.5rem]"
      : "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
    disabled
      ? "cursor-not-allowed border-gray-200 bg-gray-50/70 opacity-60"
      : selected
      ? "cursor-pointer bg-white shadow-sm"
      : "cursor-pointer border-gray-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)] hover:border-gray-300 hover:shadow-md",
  ].join(" ");

  const cardStyle = selected && !disabled ? { borderColor: theme.primary } : undefined;

  const priceEl = (
    <span
      className={`inline-block text-xs sm:text-sm font-semibold rounded-lg px-2.5 py-1 ${
        module.required
          ? "bg-gray-100 text-gray-900"
          : selected
          ? "bg-emerald-50 text-emerald-700"
          : "bg-gray-50 text-emerald-600"
      }`}
    >
      {module.price}
    </span>
  );

  const inner = isStacked ? (
    <>
      <div>
        <div className="flex items-center gap-3">
          <ModuleCheckbox
            inputName={inputName}
            checked={checked}
            disabled={disabled}
            lockedRequired={lockedRequired}
            onChange={onChange}
            theme={theme}
          />
          <ModuleIcon
            module={module}
            selected={selected}
            disabled={disabled}
            theme={theme}
          />
          <h3 className="min-w-0 flex-1 text-sm sm:text-base font-semibold text-gray-900 leading-snug">
            {module.label}
          </h3>
        </div>
        <p className="mt-2.5 text-xs sm:text-sm leading-relaxed text-gray-500">
          {module.description}
        </p>
      </div>
      <div className="mt-3.5 flex items-center justify-end border-t border-gray-100/80 pt-2.5">
        {priceEl}
      </div>
    </>
  ) : (
    <>
      <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-3.5">
        <ModuleCheckbox
          inputName={inputName}
          checked={checked}
          disabled={disabled}
          lockedRequired={lockedRequired}
          onChange={onChange}
          theme={theme}
        />
        <ModuleIcon
          module={module}
          selected={selected}
          disabled={disabled}
          theme={theme}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900">{module.label}</h3>
            {module.required && lockedRequired ? (
              <span
                className="rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
                style={{ backgroundColor: theme.primary }}
              >
                Required
              </span>
            ) : null}
          </div>
          <p className="mt-1 max-w-none text-xs sm:text-sm leading-relaxed text-gray-500">
            {module.description}
          </p>
        </div>
      </div>
      <div className="shrink-0 pt-2 sm:pt-0 sm:pl-4 sm:text-right">{priceEl}</div>
    </>
  );

  if (disabled) {
    return (
      <div className={cardClass} style={cardStyle}>
        {inner}
      </div>
    );
  }

  return (
    <label className={cardClass} style={cardStyle}>
      {inner}
    </label>
  );
}

export function BillingNotice({ chargeImmediately, provider }) {
  const theme = getProviderTheme(provider);

  if (chargeImmediately) {
    return (
      <div
        className="rounded-lg border px-3.5 py-2.5 text-xs"
        style={{
          backgroundColor: "#FFFBEB",
          borderColor: theme.primary,
          color: "#78350f",
        }}
      >
        <p className="font-semibold text-amber-900">Charged today</p>
        <p className="mt-0.5 leading-relaxed">
          This organization has already used its free trial, so your default
          payment method will be charged immediately when you subscribe, then
          monthly on autopay.
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border px-3.5 py-2.5 text-xs"
      style={{
        backgroundColor: theme.light,
        borderColor: theme.primary,
        color: "#14532d",
      }}
    >
      <p className="m-0 leading-relaxed">{NEW_CUSTOMER_BILLING_COMPACT_LINE}</p>
    </div>
  );
}

/**
 * Shared module selection cards used by single-org and multi-org subscribe flows.
 */
export default function SubscribeModulePicker({
  provider = "xero",
  orgCountryCode,
  subPlanFormData,
  onSubPlanChange,
  accountsRequired = true,
  showPricingSummary = true,
  chargeImmediately = false,
  introText,
}) {
  const isQbo = provider === "qbo";
  const total = computeSubscribeMonthlyTotal(subPlanFormData);
  const showPayroll = !isQbo && isPayrollSubscribeEligible(orgCountryCode);
  const isAccountSelected = !!subPlanFormData.accounts;
  const accountsDisabled = accountsRequired;
  const accountModule = accountsRequired
    ? SUBSCRIBE_MODULES.account
    : { ...SUBSCRIBE_MODULES.account, required: false };

  const baseAccountCost = 9.95;
  const filesCost = !isQbo && subPlanFormData.files ? 4.0 : 0;
  const historyNotesCost = !isQbo && subPlanFormData.history_and_notes ? 4.0 : 0;
  const basePlanCost =
    (isAccountSelected ? baseAccountCost : 0) + filesCost + historyNotesCost;

  const retentionDays = Number(subPlanFormData.retention_days) || 7;
  const extraRetentionDays = Math.max(0, retentionDays - 7);

  const handleChange = (e) => {
    if (e?.patch) {
      onSubPlanChange?.(e);
      return;
    }
    const { name, checked, value } = e.target;
    if (name === "retention_days") {
      onSubPlanChange?.({
        target: { name: "retention_days", value: Number(value) },
        patch: { retention_days: Number(value) },
      });
      return;
    }
    if (name === "accounts" && !checked) {
      onSubPlanChange?.({
        target: { name: "accounts", checked: false },
        patch: {
          accounts: false,
          files: false,
          history_and_notes: false,
          payroll: false,
          retention_days: 7,
          skipped: true,
        },
      });
    } else if (name === "accounts" && checked) {
      onSubPlanChange?.({
        target: { name: "accounts", checked: true },
        patch: {
          accounts: true,
          skipped: false,
        },
      });
    } else {
      if (!isAccountSelected) return;
      onSubPlanChange?.({
        target: { name, checked },
      });
    }
  };

  return (
    <div>
      {introText !== null && (
        <p className="text-xs sm:text-sm leading-relaxed text-gray-600">
          {introText ?? getSubscribeIntro(provider)}
        </p>
      )}

      <div className={`space-y-3 sm:space-y-3.5 ${introText !== null ? "mt-4" : ""}`}>
        <ModuleOptionCard
          moduleKey="accounts"
          module={accountModule}
          checked={subPlanFormData.accounts}
          disabled={accountsDisabled}
          lockedRequired={accountsDisabled}
          onChange={handleChange}
          provider={provider}
        />

        {!isQbo && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-3.5">
            <ModuleOptionCard
              moduleKey="files"
              module={SUBSCRIBE_MODULES.files}
              checked={isAccountSelected && !!subPlanFormData.files}
              disabled={!isAccountSelected}
              onChange={handleChange}
              provider={provider}
              layout="stacked"
            />
            <ModuleOptionCard
              moduleKey="history_and_notes"
              module={SUBSCRIBE_MODULES.history_and_notes}
              checked={isAccountSelected && !!subPlanFormData.history_and_notes}
              disabled={!isAccountSelected}
              onChange={handleChange}
              provider={provider}
              layout="stacked"
            />
          </div>
        )}

        {showPayroll && (
          <ModuleOptionCard
            moduleKey="payroll"
            module={SUBSCRIBE_MODULES.payroll}
            checked={isAccountSelected && !!subPlanFormData.payroll}
            disabled={!isAccountSelected}
            onChange={handleChange}
            provider={provider}
          />
        )}

        {/* Rolling Backup Retention Selector */}
        {isAccountSelected && (
          <div className="rounded-2xl border-2 border-gray-200 bg-white p-3.5 sm:p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-all">
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-xs sm:text-base font-semibold text-gray-900">
                  Rolling Backup Retention
                </span>
              </div>
              <span className="text-[11px] font-medium text-gray-500">
                Range: 7 to 60 Days
              </span>
            </div>

            {/* Quick Presets */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {RETENTION_PRESETS.map((item) => {
                const isSelected = retentionDays === item.days;
                return (
                  <button
                    key={item.days}
                    type="button"
                    onClick={() =>
                      handleChange({
                        target: { name: "retention_days", value: item.days },
                        patch: { retention_days: item.days },
                      })
                    }
                    className={`flex flex-col items-center justify-center p-2 rounded-xl border text-center transition-all cursor-pointer ${
                      isSelected
                        ? "border-blue-600 bg-blue-50/70 text-blue-900 ring-2 ring-blue-500/20 shadow-sm"
                        : "border-gray-200 bg-white hover:border-gray-300 text-gray-700"
                    }`}
                  >
                    <span className="text-xs sm:text-sm font-bold">{item.label}</span>
                    <span
                      className={`text-[10px] sm:text-[11px] mt-0.5 font-medium ${
                        item.extraCost === 0
                          ? "text-green-600 font-semibold"
                          : isSelected
                          ? "text-blue-700 font-semibold"
                          : "text-gray-500"
                      }`}
                    >
                      {item.extraCost === 0
                        ? `$${basePlanCost.toFixed(2)}/mo`
                        : `+$${item.extraCost}/mo`}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Slider Component */}
            <div className="bg-gray-50/70 p-3 sm:p-3.5 rounded-xl border border-gray-200 mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-600">
                  Adjust days slider:
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="text-lg sm:text-xl font-bold text-blue-600">
                    {retentionDays}
                  </span>
                  <span className="text-xs font-medium text-gray-500">
                    Days Retention
                  </span>
                </div>
              </div>

              <Slider
                min={7}
                max={60}
                step={1}
                value={retentionDays}
                onChange={(val) =>
                  handleChange({
                    target: { name: "retention_days", value: val },
                    patch: { retention_days: val },
                  })
                }
                marks={RETENTION_SLIDER_MARKS}
                tooltip={{
                  formatter: (val) => `${val} Days`,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {showPricingSummary && subPlanFormData.accounts && (
        <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50/50 p-4 sm:p-5 space-y-2">
          {!isQbo && (
            <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600">
              <span>Base plan (Account):</span>
              <span className="font-semibold text-gray-900">$9.95/month</span>
            </div>
          )}
          {!isQbo && subPlanFormData.files && (
            <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600">
              <span>Files module:</span>
              <span className="font-semibold text-gray-900">+$4.00/month</span>
            </div>
          )}
          {!isQbo && subPlanFormData.history_and_notes && (
            <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600">
              <span>History & Notes module:</span>
              <span className="font-semibold text-gray-900">+$4.00/month</span>
            </div>
          )}
          {!isQbo && subPlanFormData.payroll && showPayroll && (
            <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600">
              <span>Payroll module:</span>
              <span className="font-semibold text-emerald-600">Included ($0.00)</span>
            </div>
          )}
          {extraRetentionDays > 0 && (
            <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600">
              <span>Extended retention ({retentionDays} days):</span>
              <span className="font-semibold text-blue-700">
                +${(extraRetentionDays * 2).toFixed(2)}/month
              </span>
            </div>
          )}
          {subPlanFormData.coupon_discount && (
            <div className="flex items-center justify-between text-xs sm:text-sm text-emerald-600 font-medium">
              <span>Coupon discount:</span>
              <span>
                {subPlanFormData.coupon_discount.percent_off != null
                  ? `-${subPlanFormData.coupon_discount.percent_off}%`
                  : `-$${Number(subPlanFormData.coupon_discount.amount_off).toFixed(2)}`}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between text-base sm:text-lg font-bold text-gray-900 mt-2 pt-2 border-t border-gray-200/60">
            <span>Total monthly cost:</span>
            <span className="text-blue-700">${total.toFixed(2)}/month</span>
          </div>
          <div className="mt-3">
            <BillingNotice
              chargeImmediately={chargeImmediately}
              provider={provider}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export { getProviderTheme };
